import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Skeleton, Result, Button, Divider, Space } from 'antd';
import { SolutionOutlined, EnvironmentOutlined } from '@ant-design/icons';
import AuthShell from '../../ui/AuthShell';
import Card from '../../ui/Card';
import AuthHeader from '../../ui/AuthHeader';
import { decryptLinkPayload, getEncryptionKey } from '../../utils/encryption';
import AppStoreLinks from '../../components/AppStoreLinks';

const { Title, Text } = Typography;

const decryptAssignmentLink = async (encryptedPart, key) => {
    try {
        const decryptedText = await decryptLinkPayload(encryptedPart, key);
        if (!decryptedText) throw new Error('Decryption returned empty.');

        const parts = decryptedText.split('||');
        if (parts.length < 3) throw new Error('Invalid payload structure.');

        return {
            id: parts[0],
            role: parts[1],
            clientName: parts[2],
        };
    } catch (error) {
        return { error: error.message || error.toString() };
    }
};

const AssignmentPublicDetails = () => {
    const { id: routeId } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [showData, setShowData] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearTimeout(timeoutId);
                if (isMounted) setShowData(true);
            }
        };

        const loadData = async () => {
            if (!routeId) {
                setLoading(false);
                if (isMounted) setShowData(true);
                return;
            }

            const key = getEncryptionKey();
            const resultData = await decryptAssignmentLink(routeId, key);

            if (!isMounted) return;

            setData(resultData);
            setLoading(false);

            if (resultData && resultData.error) {
                setShowData(true);
                return;
            }

            if (resultData && resultData.id) {
                document.addEventListener('visibilitychange', handleVisibilityChange);
                // Attempt to open the app via deep link immediately
                const appUrl = `vitalstaf://assignment/${resultData.id}`;
                window.location.href = appUrl;

                // Fallback to showing web data if the app is not installed or Safari blocks the auto-redirect
                timeoutId = setTimeout(() => {
                    if (isMounted) setShowData(true);
                }, 3000);
            } else {
                setShowData(true);
            }
        };

        loadData();

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [routeId]);

    if (loading) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!showData) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Title level={4}>Opening in VitalStaf App...</Title>
                    <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!data || data.error) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Result
                        status="error"
                        title="Link Expired or Invalid"
                        subTitle={data && data.error ? `Error: ${data.error}` : "Sorry, we couldn't load the assignment details for this link."}
                        extra={[
                            <Link key="home" to="/">
                                <Button type="primary" className="vs-btn vs-btn-primary">Go to Homepage</Button>
                            </Link>
                        ]}
                    />
                </Card>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <Card style={{ padding: 32, minWidth: '400px', maxWidth: '500px' }}>
                <AuthHeader
                    pillText="VitalStaf Assignment"
                    title="Assignment Updates"
                    subtitle="Review your latest assignment details."
                />

                <Divider />

                <Title level={3} style={{ marginTop: 0, marginBottom: 24, color: '#0f453f' }}>
                    {data.role}
                </Title>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <EnvironmentOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                        <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Client / Facility</Text>
                            <Text strong>{data.clientName || 'Not specified'}</Text>
                        </div>
                    </div>
                </Space>

                <Divider style={{ margin: '32px 0 24px 0' }} />

                <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
                    <Button
                        type="default"
                        size="large"
                        block
                        className="vs-btn"
                        style={{ height: 48, fontSize: 16 }}
                        onClick={() => { window.location.href = `vitalstaf://assignment/${data.id}`; }}
                    >
                        Open in VitalStaf App
                    </Button>

                    <Link to="/auth/login" style={{ width: '100%' }}>
                        <Button type="primary" size="large" block className="vs-btn vs-btn-primary" style={{ height: 48, fontSize: 16 }}>
                            Sign in to View Details
                        </Button>
                    </Link>
                </Space>
                <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">
                        Don't have an account? <Link to="/auth/signup">Sign up</Link>
                    </Text>
                </div>
                <AppStoreLinks />
            </Card>
        </AuthShell>
    );
};

export default AssignmentPublicDetails;
